import { motion } from 'framer-motion';
import { SUPPORT_EMAIL } from 'src/settings';

import { Typography } from 'src/shared/components/ui';

interface RegisterSuccessMessageProps {
  openSignUp: boolean;
  /** The email address to display. Falls back to generic wording when undefined (e.g. direct page visit). */
  email?: string;
}

/**
 * Renders the post-registration confirmation message.
 *
 * Two variants exist based on the `open_sign_up` flag:
 * - Closed signup (waitlist): a simple "thank you" notice.
 * - Open signup: mail icon + "sign-in link mailed" heading + inbox instructions.
 */
const RegisterSuccessMessage: React.FC<RegisterSuccessMessageProps> = ({
  openSignUp,
  email
}) => {
  if (!openSignUp) {
    return (
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ ease: 'easeInOut', duration: 1 }}
        data-testid="auth-form-confirmation"
        className="mt-10"
      >
        <Typography variant="h2" weight="bold">
          Thank you for signing up, we will be in touch soon!
        </Typography>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ ease: 'easeInOut', duration: 1 }}
      data-testid="auth-form-confirmation"
      className="mt-10"
    >
      <img src="/icons/mail.svg" alt="mail" className="mb-4 size-16" />
      <Typography variant="h2" weight="bold">
        Your sign-in link has been mailed to{' '}
        <span className="text-p-cavela-blue">
          {email ?? 'your email address'}
        </span>
      </Typography>

      <Typography className="text-black mt-6" variant="body-1">
        Please check your inbox and use the link in the e-mail to access your
        account. If you don&apos;t see the e-mail, check your spam folder or{' '}
        <Typography
          as="a"
          variant="body-1"
          onClick={() => window.open(`mailto:${SUPPORT_EMAIL}`, '_blank')}
          className="text-primary hover:opacity-40 cursor-pointer inline-block"
        >
          contact us.
        </Typography>
      </Typography>
    </motion.div>
  );
};

export default RegisterSuccessMessage;
